/**
 * datasetValidation.js — Dataset-flavored usage lookups and non-blocking
 * validation warnings for the Dataset Library. Built on the generic
 * resourceUsage.js walker (parameterized by the 'dataset' property name).
 */

import { DATASET_TYPES } from '../stores/datasetStore.js'
import {
  findWidgetsReferencing,
  findWidgetsMissingProp,
  findDanglingReferences,
} from './resourceUsage.js'

const VALID_TYPES = new Set(DATASET_TYPES.map(t => t.value))
const TYPE_UNIT = new Map(DATASET_TYPES.map(t => [t.value, t.unit]))

/** Widgets on any page that currently link to this dataset uuid. */
export function findWidgetsUsingDataset(layer, uuid) {
  return findWidgetsReferencing(layer, 'dataset', uuid)
}

/** Dataset-driven widgets (schema has a `dataset` prop) with no link set. */
export function findDatasetDrivenWidgetsWithoutDataset(layer) {
  // Gauge's object-level Dataset is optional; manual/legacy Gauges are valid
  // without one. Chart and Legend keep the existing required-resource warning.
  return findWidgetsMissingProp(layer, 'dataset').filter(item => item.obj !== 'gauge')
}

/** Widgets whose linked dataset uuid no longer exists in the library. */
export function findDanglingDatasetRefs(layer, datasetStore) {
  return findDanglingReferences(layer, 'dataset', (uuid) => datasetStore.getDataset(uuid))
}

/** Non-blocking issues for a single dataset. */
export function getDatasetWarnings(dataset, allDatasets) {
  const warnings = []

  if (!dataset.series || dataset.series.length === 0) {
    warnings.push('No series defined')
  }

  const dupe = (allDatasets ?? []).find(d => d.uuid !== dataset.uuid && d.id === dataset.id)
  if (dupe) {
    warnings.push(`Duplicate runtime ID ${dataset.id} (also used by "${dupe.name}")`)
  }

  if (!VALID_TYPES.has(dataset.type)) {
    warnings.push(`Invalid type "${dataset.type}"`)
  } else if (!dataset.unit && TYPE_UNIT.get(dataset.type) !== '') {
    warnings.push('Missing unit')
  }

  return warnings
}
